import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { createTheme, ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Firebase from "./Firebase/Firebase";
import { BarChart, LineChart, PieChart } from "@mui/x-charts";
import {
  Datagrid,
  FunctionField,
  List,
  ResourceContextProvider,
  TextField,
  useGetOne,
  usePermissions,
} from "react-admin";
import { Link as MuiLink, Link } from "@mui/material";
import { Article } from "@mui/icons-material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { CloseFullscreen } from "@mui/icons-material";
import { SelectProviderInput } from "./SelectProviderIntput";
import fakeAnalysis from "./components/datafakes/fakeAnalysis";
import UpgradeStickyBanner from "./utils/UpgradeStickyBanner";
import UpgradePlanModal from "./components/UpgradePlanModal";
import fakeTopCustomers from "./components/datafakes/fakeTopCustomers";
import ChartSkeleton from "./components/ChartSkeleton";
import ChartSkeletonPie from "./components/ChartSkeletonPie";
import ChartSkeletonBar from "./components/ChartSkeletonBar";
import ChartSkeletonHorizontalBar from "./components/ChartSkeletonHorizontalBar";
import StatCardSkeleton from "./components/StatCardSkeleton";
import { useBirthdaysNotifications } from "./hooks/useBirthdaysNotifications";
import { useContext } from "react";
import { OnboardingContext } from "./contexts/OnboardingContext";
import { updateOnboardingStep } from "./components/dataProvider";

function useSize() {
  const ref = useRef(null);
  const getSize = () => ({
    width: ref.current?.clientWidth || 0,
    height: ref.current?.clientHeight || 0,
  });
  const [size, setSize] = useState(getSize);

  useLayoutEffect(() => {
    function updateSize() {
      setSize(getSize());
    }
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return [ref, size];
}

const Dashboard = () => {
  useBirthdaysNotifications();

  const muiTheme = createTheme();
  const [dateFilter, setDateFilter] = useState("año");
  const [, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const perfil = localStorage.getItem("profile");
  const profile = perfil ? JSON.parse(perfil) : null;
  const { permissions } = usePermissions();

  const defaultBranch = profile?.role === "admin-sucursal" && profile?.branch ? profile?.branch._id : "";
  const computedProviderId = profile?.role === "admin-sucursal" ? profile?.employer._id : null;
  const [selectedBranch, setSelectedBranch] = useState(defaultBranch);

  const [selectedProvider, setSelectedProvider] = useState("");
  const { data: user, refetch } = useGetOne("availability", {
    id: profile?.role === "admin-sucursal" ? profile?.employer._id : profile?._id,
  });
  const plan = user?.plan;
  const [branches, setBranches] = useState([]);
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const { setOnboardingSteps, onboardingSteps, checkAndTriggerOnboarding } = useContext(OnboardingContext);

  // Onboarding modal control
  const [openOnboarding, setOpenOnboarding] = useState(false);
  useEffect(() => {
    if (user && Array.isArray(user.onboardingSteps) && user.onboardingSteps.length === 0) {
      setOpenOnboarding(true);
    } else {
      setOpenOnboarding(false);
    }
  }, [user]);

  const handleOnboardingConfirm = async () => {
    try {
      await updateOnboardingStep(
        { userId: user._id, step: 1 },
        { setOnboardingSteps, checkAndTriggerOnboarding, refetch, onboardingSteps }
      );
      setOpenOnboarding(false);
    } catch (err) {
      // Manejo de error
      alert("Error actualizando el onboarding, intenta de nuevo.");
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      if (!user || branchesLoaded || user.plan === "freemium") return;

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/enterprise/branches?page=1&perPage=50&sort=name&status=true`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        setBranches(result.data); // asume que el API devuelve array directo
      } catch (error) {
        console.error("Error al obtener sucursales:", error);
      }
    };

    fetchBranches();
    setBranchesLoaded(true);
  }, [user?._id, user?.plan, branchesLoaded]);

  const fetchEmployees = async () => {
    const token = localStorage.getItem("token");
    setIsLoading(true);
    const rangeMap = { dia: "day", semana: "week", mes: "month", año: "year" };
    const backendRange = rangeMap[dateFilter] || "month";
    const branchParam = selectedBranch ? `&branchId=${selectedBranch}` : "";

    let providerId = "";
    if (permissions === "admin-sucursal") {
      providerId = profile.employer._id;
    } else if (permissions === "admin") {
      providerId = selectedProvider;
    } else {
      providerId = profile._id;
    }

    try {
      if (plan === "freemium") {
        setAnalytics(fakeAnalysis);
      } else {
        const url = `${process.env.REACT_APP_API_URL}/dashboard/analytics?providerId=${providerId}${branchParam}&range=${backendRange}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setAnalytics(data.data);

        // Aquí cargamos topCustomers directamente
        if (data.data?.topCustomers?.length) {
          await fetchFullCustomers(data.data.topCustomers);
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFullCustomers = async (topCustomers) => {
    try {
      if (plan === "freemium") {
        setTopCustomersDetail(fakeTopCustomers);
        return;
      }
      const token = localStorage.getItem("token");
      const customersPayload = topCustomers.map((c) => ({
        id: c._id,
        totalservices: c.totalServices,
      }));
      const bodyPayload = {
        customers: customersPayload,
        ...(permissions === "admin" && selectedProvider ? { providerId: selectedProvider } : {}),
      };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/enterprise/customers/byIds`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyPayload),
      });
      if (!response.ok) throw new Error("Error al obtener datos de clientes");
      const result = await response.json();
      setTopCustomersDetail(result);
    } catch (error) {
      console.error("Error en fetchFullCustomers:", error);
    }
  };

  useEffect(() => {
    if (!user?._id) return;
    fetchEmployees();
  }, [dateFilter, selectedBranch, selectedProvider, permissions, user?._id, plan]);

  const [topCustomersDetail, setTopCustomersDetail] = useState([]);

  const ResponsiveLineChart = ({
    title,
    height,
    curve,
    isFullScreen,
    onToggleFullScreen,
    containerRef,
    isFreemium = false,
    onUpgradeClick,
  }) => {
    const [ref, size] = useSize();

    const lineChartData = useMemo(() => {
      if (!analytics || !analytics.lineData) {
        return null; // devolvemos null o datos vacíos
      }

      return {
        xAxis: [
          {
            scaleType: "band",
            data: analytics.lineData.xAxis.labels,
          },
        ],
        series: [
          {
            label: "Ingresos",
            data: analytics.lineData.series.ingresos,
            curve,
            color: "#6ee7b7",
            areaOpacity: 0.1,
            lineWidth: 0,
          },
          {
            label: "Egresos",
            data: analytics.lineData.series.egresos,
            curve,
            color: "#f59e0b",
            areaOpacity: 0.1,
            lineWidth: 0,
          },
          {
            label: "Ganancias",
            data: analytics.lineData.series.ganancias,
            curve,
            color: "rgb(65, 120, 193)",
            areaOpacity: 0.1,
            lineWidth: 2,
            className: "area-ganancias",
          },
        ],
      };
    }, [analytics, curve]);

    if (!analytics || !analytics.lineData) {
      return <ChartSkeleton height={height} />;
    }
    // Si estamos en modo fullscreen, ajustamos el <Card> a todo el contenedor
    const fullscreenStyles = isFullScreen
      ? {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 9999,
          width: "100%",
          height: containerRef.current?.clientHeight || "100%",
          backgroundColor: "#fff",
          transform: "scale(1)", // al estar en fullscreen, vuelve al 100%
        }
      : {
          transform: "scale(0.95)", // cuando NO está fullscreen, está un poquito “contraído”
        };

    return (
      <Card
        sx={{
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          ...fullscreenStyles,
        }}
      >
        <CardHeader
          title={title}
          action={
            <IconButton onClick={isFreemium ? onUpgradeClick : onToggleFullScreen} size="small">
              {isFullScreen ? <CloseFullscreen /> : <FullscreenIcon />}
            </IconButton>
          }
          style={{ paddingBottom: 0 }} // si quieres menos espacio
        />
        <CardContent
          ref={ref}
          sx={{
            height: isFullScreen ? height + 300 : height,
            position: "relative",
            paddingTop: 0,
            opacity: isFreemium ? 0.5 : 1,
            pointerEvents: isFreemium ? "none" : "auto",
            // filter: isFreemium ? "blur(1px)" : "none",
            transition: "all 0.3s ease",
          }}
        >
          <LineChart
            width={size.width}
            height={isFullScreen ? size.height : size.height}
            xAxis={lineChartData.xAxis}
            series={lineChartData.series}
            sx={{
              "& .MuiAreaElement-series-0": {
                fill: "rgb(65, 120, 193)",
                fillOpacity: 0.1,
              },
              "& .MuiAreaElement-series-1": {
                fill: "#f59e0b",
                fillOpacity: 0.1,
              },
              "& .MuiMarkElement-root.MuiMarkElement-series-auto-generated-id-0": {
                fill: "transparent",
                stroke: "transparent",
                strokeWidth: 0,
              },
              "& .MuiMarkElement-root.MuiMarkElement-series-auto-generated-id-1": {
                fill: "transparent",
                stroke: "transparent",
                strokeWidth: 0,
              },
              "& .MuiLineElement-root.MuiLineElement-series-auto-generated-id-0": {
                stroke: "transparent",
              },
              "& .MuiLineElement-root.MuiLineElement-series-auto-generated-id-1": {
                stroke: "transparent",
              },
            }}
          />
        </CardContent>
      </Card>
    );
  };

  const paletteDevice = [
    "#FF770D", // color principal (naranja fuerte)
    "#FFA04D", // naranja suave
    "#FFC18C", // aún más suave
    "#FFE0C2", // muy suave
    "#FFF1E4", // casi blanco con tinte naranja
    "#e21d48", //rojo fuerte
    "#e9536f",
    "#f7abb6",
    "#f17e92",
    "#fbd5da",
  ];
  // const paletteBrowser = ["#e21d48", "#fbd5da", "#f17e92", "#f7abb6", "#e9536f"];
  const paletteBrowser = [
    "#4178C1", // azul principal
    "#73A0D5", // más suave
    "#A1C1E5", // aún más suave
    "#D2E4F6", // muy suave
    "#EEF5FC", // casi blanco
    "#4c1d95", // violeta fuerte (color principal)
    "#7b44b5", // violeta medio
    "#a678d3", // violeta suave
    "#ceb0e9", // muy suave
    "#f1e9fb", // casi blanco con tinte violeta
  ];

  const browserData = [
    { label: "Chrome", value: 61 },
    { label: "Mozilla", value: 29 },
    { label: "Safari", value: 7.5 },
    { label: "Others", value: 2.5 },
  ];
  const ResponsiveDonutCard = ({
    title,
    data,
    palette,
    radius,
    isFullScreen,
    onToggleFullScreen,
    containerRef,
    isFreemium = false,
    onUpgradeClick,
    infoService = true,
  }) => {
    const [ref, size] = useSize();
    const [legendPage, setLegendPage] = useState(0);
    const itemsPerPage = 9;
    const totalPages = Math.ceil(data.length / itemsPerPage);

    const legendSlice = useMemo(() => {
      const start = legendPage * itemsPerPage;
      const end = start + itemsPerPage;
      return data.map((item, idx) => ({ ...item, originalIdx: idx })).slice(start, end);
    }, [data, legendPage]);

    const fullscreenStyles = useMemo(() => {
      if (!isFullScreen) return {};
      return {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 9999,
        width: containerRef.current?.clientWidth || "100%",
        height: containerRef.current?.clientHeight || "100%",
        backgroundColor: "#fff",
      };
    }, [isFullScreen, containerRef]);

    const pieSeries = useMemo(() => {
      return [
        {
          data: data.map((item, idx) => ({
            id: idx,
            value: item.value,
            label: item.label,
            color: palette[idx % palette.length],
            percentage: item.percentage,
            averageAge: item.averageAge,
          })),
          valueFormatter: (v, { dataIndex }) => {
            const { percentage, averageAge } = data[dataIndex];
            const serviciosStr = `📦 ${v.value} servicios`;
            const porcentajeStr = percentage != null ? `📊 ${percentage}%` : "";
            const edadStr = averageAge != null ? `👤 ${averageAge} años` : "";
            if (!infoService) return [porcentajeStr, edadStr].filter(Boolean).join(" | ");
            return [serviciosStr, porcentajeStr, edadStr].filter(Boolean).join(" | ");
          },
          innerRadius: radius,
        },
      ];
    }, [data, palette, radius, infoService]);

    if (!analytics || !analytics.lineData) {
      return <ChartSkeletonPie size={250}></ChartSkeletonPie>;
    }

    return (
      <Card
        sx={{
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          ...fullscreenStyles,
        }}
      >
        <CardHeader
          title={title}
          action={
            <IconButton onClick={isFreemium ? onUpgradeClick : onToggleFullScreen} size="small">
              {isFullScreen ? <CloseFullscreenIcon /> : <FullscreenIcon />}
            </IconButton>
          }
        />
        <CardContent
          ref={ref}
          sx={{
            height: isFullScreen ? size.height + 300 : 290,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            opacity: isFreemium ? 0.5 : 1,
            pointerEvents: isFreemium ? "none" : "auto",
            // filter: isFreemium ? "blur(1px)" : "none",
            transition: "all 0.3s ease",
          }}
        >
          <Box sx={{ ml: 3.5, backgroundColor: "#fff" }}>
            <PieChart
              width={size.width}
              height={isFullScreen ? size.height * 0.7 + 200 : size.height * 0.7}
              series={pieSeries}
              /* ⑤ desactivamos la leyenda que no necesitas */
              sx={{
                /* —––––– gráfico —––––– */
                "& .MuiChartsLegend-root": { display: "none" },
                "& .MuiChartsArcLabel-root": { fontWeight: 600 },

                /* —––––– tooltip: forzamos saltos de línea y algo de estilo —––––– */
                "& .css-42val2-MuiPopper-root-MuiChartsTooltip-root": {
                  whiteSpace: "pre-line", // respeta los \n
                  fontSize: "0.875rem",
                  lineHeight: 1.35,
                  fontWeight: 500,
                  display: "flex",
                },
                "& .MuiChartsTooltip-root": {
                  borderRadius: 8,
                  boxShadow: "0 3px 12px rgba(0,0,0,.15)",
                  padding: "8px 12px",
                },
              }}
              /* ⑥ forzamos tooltip por ítem (por defecto ya lo es) */
              slotProps={{
                tooltip: {
                  trigger: "item",
                  // Forzamos la visibilidad del tooltip del primer item (índice 0)
                  open: true,
                  selectedItemIndex: 0, // Cambia esto al índice que quieras ver
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isFullScreen ? "repeat(3, auto)" : "repeat(2, auto)",
              columnGap: 2,
              rowGap: 1,
              mt: 2,
              justifyContent: "space-between",
              px: 2,
            }}
          >
            {legendSlice.map(({ label, originalIdx }) => (
              <Box key={originalIdx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: palette[originalIdx % palette.length],
                  }}
                />
                <Typography variant="body2">{label}</Typography>
              </Box>
            ))}
          </Box>

          {/* BOTONES «ANTERIOR» / «SIGUIENTE» SOLO EN FULLSCREEN Y SI HAY +10 ÍTEMS */}
          {isFullScreen && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 1.5,
              }}
            >
              <IconButton
                size="small"
                disabled={legendPage === 0}
                onClick={() => setLegendPage((p) => Math.max(p - 1, 0))}
              >
                ◀︎
              </IconButton>
              <Typography variant="caption" sx={{ alignSelf: "center" }}>
                {legendPage + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={legendPage === totalPages - 1}
                onClick={() => setLegendPage((p) => Math.min(p + 1, totalPages - 1))}
              >
                ▶︎
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  const generateUniqueShortNames = (fullNames) => {
    const usedNames = new Set();
    const result = [];

    for (const fullName of fullNames) {
      const words = fullName.split(" ");
      let shortName = "";
      let i = 0;

      // Intenta usar una palabra distinta hasta encontrar una no repetida
      while (i < words.length) {
        if (!usedNames.has(words[i])) {
          shortName = words[i];
          break;
        }
        i++;
      }

      // Si todas las palabras están repetidas, agrega sufijo numérico
      if (!shortName) {
        const base = words[0]; // o puedes usar el fullName
        let j = 1;
        while (usedNames.has(`${base}${j}`)) {
          j++;
        }
        shortName = `${base}${j}`;
      }

      usedNames.add(shortName);
      result.push(shortName);
    }

    return result;
  };
  function generarDatosFakePieChart(n = 50, labelBase = "Servicio") {
    const data = [];
    let total = 0;

    // Primero generamos valores aleatorios
    for (let i = 0; i < n; i++) {
      const valor = Math.floor(Math.random() * 100) + 1; // entre 1 y 100
      total += valor;
      data.push({
        label: `${labelBase} ${i + 1}`,
        value: valor,
      });
    }

    // Luego calculamos el porcentaje
    return data.map((item) => ({
      ...item,
      percentage: ((item.value / total) * 100).toFixed(1), // opcional
      averageAge: Math.floor(Math.random() * 40) + 18, // edad aleatoria entre 18 y 58
    }));
  }

  const makeNamesUnique = (names) => {
    const counts = {};
    return names.map((name) => {
      if (!counts[name]) counts[name] = 0;
      if (counts[name] === 0) {
        counts[name] += 1;
        return name;
      }
      const uniqueName = `${name}(${counts[name]})`;
      counts[name] += 1;
      return uniqueName;
    });
  };

  // Componente para el BarChart "responsive" (ejemplo)
  const ResponsiveBarChart = ({
    title,
    isFullScreen,
    onToggleFullScreen,
    containerRef,
    isFreemium = false,
    onUpgradeClick,
  }) => {
    const [ref, size] = useSize();

    const fullscreenStyles = isFullScreen
      ? {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 9999,
          width: containerRef.current?.clientWidth || "100%",
          height: containerRef.current?.clientHeight || "100%",
          backgroundColor: "#fff",
          // Podrías ocultar overflow u otros ajustes
          // overflow: "auto"
        }
      : {};

    // Datos simulados por empleado
    const { nombresUnicos, servicios, calificaciones, retentionRate } = useMemo(() => {
      if (!analytics?.barData) {
        return {
          nombresUnicos: [],
          servicios: [],
          calificaciones: [],
          retentionRate: [],
        };
      }

      const empleados = analytics.barData.xAxis.employees;
      return {
        nombresUnicos: makeNamesUnique(empleados),
        servicios: analytics.barData.series.servicios,
        calificaciones: analytics.barData.series.calificacion,
        retentionRate: analytics.barData.series.retentionRate,
      };
    }, [analytics]);

    if (!analytics || !analytics.lineData) {
      return <ChartSkeletonBar></ChartSkeletonBar>;
    }

    return (
      <Card
        sx={{
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          ...fullscreenStyles,
        }}
      >
        <CardHeader
          title={title}
          action={
            <IconButton onClick={isFreemium ? onUpgradeClick : onToggleFullScreen} size="small">
              {isFullScreen ? <CloseFullscreen /> : <FullscreenIcon />}
            </IconButton>
          }
          style={{ paddingBottom: 0 }} // si quieres menos espacio
        />
        {/* <CardHeader title={title} style={{ paddingBottom: 0 }} /> */}
        {/* <CardContent ref={ref} sx={{ height: 310, position: "relative", paddingTop: 0 }}> */}
        <CardContent
          ref={ref}
          sx={{
            height: isFullScreen ? 300 + 300 : 310,
            position: "relative",
            paddingTop: 0,
            opacity: isFreemium ? 0.5 : 1,
            pointerEvents: isFreemium ? "none" : "auto",
            // filter: isFreemium ? "blur(1px)" : "none",
            transition: "all 0.3s ease",
          }}
        >
          <BarChart
            width={size.width}
            height={size.height}
            xAxis={[{ scaleType: "band", data: nombresUnicos }]}
            series={[
              {
                label: "Servicios",
                data: servicios,
                stack: "total",
                color: "rgb(65, 120, 193)",
              },
              {
                label: "Calificación",
                data: calificaciones,
                stack: "total",
                color: "#f59e0b",
              },
              {
                id: "retention",
                label: "Tasa de Retención",
                data: retentionRate,
                stack: "total",
                color: "#d9ebf9ff",
                valueFormatter: (v) => `${(v || 0).toFixed(2)}%`,
              },
            ]}
            borderRadius={4}
            sx={{
              "& .MuiBarElement-root": {
                // Por ejemplo, ajustar opacidad, hover, etc.
                fillOpacity: 0.9,
              },
              "& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel": {
                fill: "#fff",
              },
            }}
            // <path class="MuiChartsAxisHighlighter-root css-wlqmut-MuiChartsAxisHighlight-root">
          />
        </CardContent>
      </Card>
    );
  };

  const ResponsiveCustomBarChart = ({
    title,
    isFullScreen,
    onToggleFullScreen,
    containerRef,
    isFreemium = false,
    onUpgradeClick,
  }) => {
    const [ref, size] = useSize();
    if (!analytics || !analytics.lineData || !topCustomersDetail) {
      return <ChartSkeletonHorizontalBar />;
    }
    const fullscreenStyles = isFullScreen
      ? {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 9999,
          width: containerRef.current?.clientWidth || "100%",
          height: containerRef.current?.clientHeight || "100%",
          backgroundColor: "#fff",
        }
      : {};
    const dataset = topCustomersDetail.map((item) => {
      return { month: item.fullName, seoul: item.totalServices };
    });

    const sortedDataset = [...dataset].sort((a, b) => b.seoul - a.seoul);
    const n = sortedDataset.length;
    const coloredDataset = sortedDataset.map((item, index) => {
      const opacity = n > 1 ? 1 - (index / (n - 1)) * (1 - 0.4) : 1;
      // return { ...item, color: `rgba(255,119,13,${opacity})` };
      return { ...item, color: `rgba(65, 120, 193,${opacity})` };
    });
    const categories = coloredDataset.map((d) => d.month);
    const categoriesnew = generateUniqueShortNames(categories);
    const values = coloredDataset.map((d) => d.seoul);

    const barColorStyles = {};
    coloredDataset.forEach((item, i) => {
      barColorStyles[`& .MuiBarElement-root:nth-of-type(${i + 1})`] = {
        fill: item.color,
      };
    });
    return (
      <Card
        sx={{
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
          ...fullscreenStyles,
        }}
      >
        <CardHeader
          title={title}
          action={
            <IconButton onClick={isFreemium ? onUpgradeClick : onToggleFullScreen} size="small">
              {isFullScreen ? <CloseFullscreen /> : <FullscreenIcon />}
            </IconButton>
          }
        />
        <CardContent
          ref={ref}
          sx={{
            height: isFullScreen ? size.height + 300 : 300,
            position: "relative",
            opacity: isFreemium ? 0.5 : 1,
            pointerEvents: isFreemium ? "none" : "auto",
            // filter: isFreemium ? "blur(1px)" : "none",
            transition: "all 0.3s ease",
          }}
        >
          <BarChart
            xAxis={[{ scaleType: "linear" }]}
            yAxis={[{ scaleType: "band", data: categoriesnew }]}
            series={[
              {
                data: values,
                label: "Servicios",
                color: "rgb(65, 120, 193)",
              },
            ]}
            layout="horizontal"
            width={size.width}
            height={isFullScreen ? size.height + 310 : size.height}
            margin={{ top: 0 }}
            slotProps={{
              legend: {
                sx: { display: "none" }, // intentar ocultar la leyenda
              },
            }}
            sx={{
              "& .MuiChartsLegend-root": {
                display: "none", // forzar invisibilidad
                position: "absolute",
                top: "-999px",
              },
              ...barColorStyles,
            }}
            tooltip={{}}
          />
        </CardContent>
      </Card>
    );
  };
  const StatCard = ({ value, label, gradient, isFreemium }) => {
    if (!analytics || !analytics.lineData) {
      return <StatCardSkeleton />;
    }
    return (
      <Card
        sx={{
          height: "100%",
          background: gradient,
          borderRadius: 3,
          color: "#fff",
          boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 3,
          px: 2,
          opacity: isFreemium ? 0.6 : 1,
          transition: "all 0.3s ease",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Card>
    );
  };
  const WhatsAppField = ({ record }) => {
    if (!record || !record.phone || record.phone === "0") {
      return (
        <Typography variant="body2" color="textSecondary">
          No tiene
        </Typography>
      );
    }

    return (
      <MuiLink
        href={`https://wa.me/${record.countryCode ? record.countryCode : "591"}${record.phone}`}
        target="_blank"
        rel="noreferrer"
        color="primary"
      >
        {record.phone}
      </MuiLink>
    );
  };
  const ServicesButton = ({ record }) => {
    return (
      <Link
        href={"#/enterprise/customers/" + record._id + "/services"}
        style={{ display: "flex", alignItems: "center" }}
      >
        <IconButton size="medium" color="primary">
          <Article fontSize="medium" />
        </IconButton>
        {/* <Typography variant="body2" color="primary">Ver</Typography> */}
      </Link>
    );
  };

  const directBookings = (analytics?.pieCharts?.totalDirectBookings || 0) * 100;
  const redirectBookings = (analytics?.pieCharts?.totalRedirectBookings || 0) * 100;
  const hasData = directBookings + redirectBookings > 0;
  const pieChartData = [
    {
      label: "Reservas Directas",
      value: directBookings,
      percentage: directBookings,
    },
    {
      label: "Reservas por Redirección",
      value: redirectBookings,
      percentage: redirectBookings,
    },
  ];

  const [isLineChartFullScreen, setIsLineChartFullScreen] = useState(false);
  const graficasContainerRef = useRef(null);

  const [isDonutSucursalFull, setIsDonutSucursalFull] = useState(false);
  const [isDonutRetentionFull, setIsDonutRetentionFull] = useState(false);
  const [isDonutDomicilioFull, setIsDonutDomicilioFull] = useState(false);

  const [isBarChartFullScreen, setIsBarChartFullScreen] = useState(false);
  const [isTopCustomersFullScreen, setIsTopCustomersFullScreen] = useState(false);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const isFreemium = user?.plan === "freemium";
  const handleDateFilterChange = (event, newFilter) => {
    if (isFreemium) {
      setOpenUpgradeModal(true);
      return;
    }
    if (newFilter !== null) {
      setDateFilter(newFilter);
    }
  };
  return (
    <Firebase>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={muiTheme}>
          <Box sx={{ p: 2 }}>
            {/* Modal de onboarding forzado */}
            {openOnboarding && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  bgcolor: "rgba(0,0,0,0.85)",
                  zIndex: 99999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 3,
                    boxShadow: 8,
                    p: 4,
                    minWidth: 350,
                    maxWidth: 500,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    ¡Bienvenido a Te Ayudo!
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {/* Video de onboarding, puedes cambiar el src por el tuyo */}
                    {/* Video de onboarding, ahora con YouTube */}
                    <iframe
                      width="100%"
                      height="250"
                      style={{ borderRadius: 8 }}
                      // src="https://www.youtube.com/embed/K9zI9y-Y_WU?autoplay=1"
                      src="https://www.youtube.com/embed/U2edS-w_YSY?autoplay=1"
                      title="Video de onboarding"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Empieza a conectar con más clientes y a potenciar tu negocio. Gestiona tus servicios, citas y pagos
                    de manera fácil y rápida. ¡Sigue los primeros pasos y descubre todo lo que Te Ayudo tiene para ti!
                  </Typography>
                  <Box>
                    <button
                      style={{
                        background: "#FF770D",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "12px 32px",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                      onClick={handleOnboardingConfirm}
                    >
                      Entendido
                    </button>
                  </Box>
                </Box>
              </Box>
            )}
            {/* Encabezado del dashboard */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Hola, {user?.first_name || ""} {user?.last_name || ""}!
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 250 }}>
                Los datos son el motor del éxito. Convierte información en acción y haz crecer tu negocio con{" "}
                <strong>Te Ayudo</strong>.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1, // Espaciado entre el select y los filtros
                }}
              >
                {
                  permissions === "admin" ? (
                    // Mostrar el select de proveedores si el usuario es admin
                    <SelectProviderInput
                      selectedProvider={selectedProvider}
                      setSelectedProvider={setSelectedProvider}
                    />
                  ) : permissions === "proveedor-empresa" ? (
                    // Mostrar el select de sucursales si el usuario es proveedor-empresa
                    <FormControl size="small" sx={{ mr: 1, marginTop: 0.5 }}>
                      <InputLabel id="branch-label">Sucursal</InputLabel>
                      <Select
                        labelId="branch-label"
                        id="branch-select"
                        label="Sucursal"
                        sx={{ width: 200 }}
                        value={selectedBranch} // tu estado local
                        onChange={(e) => {
                          const newBranchId = e.target.value;
                          setSelectedBranch(newBranchId);
                        }}
                      >
                        <MenuItem value="">
                          <em>Todas</em>
                        </MenuItem>
                        {branches?.map((branch) => (
                          <MenuItem key={branch._id} value={branch._id}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null /* No mostrar nada si no es admin ni proveedor-empresa */
                }
                <ToggleButtonGroup
                  value={dateFilter}
                  exclusive
                  onChange={handleDateFilterChange}
                  aria-label="Date Filter"
                  sx={{
                    backgroundColor: "#f3f4f6",
                    borderRadius: 2,
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                    },
                    "& button.Mui-selected": {
                      backgroundColor: "#FF770D",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#e86600",
                      },
                    },
                  }}
                >
                  <ToggleButton value="dia" aria-label="día">
                    Día
                  </ToggleButton>
                  <ToggleButton value="semana" aria-label="semana">
                    Semana
                  </ToggleButton>
                  <ToggleButton value="mes" aria-label="mes">
                    Mes
                  </ToggleButton>
                  <ToggleButton value="año" aria-label="año">
                    Año
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
            <div
              className="graficas"
              ref={graficasContainerRef}
              style={{ position: "relative", width: "100%", height: "auto" }}
            >
              {isFreemium && (
                <UpgradeStickyBanner
                  planActual="Freemium"
                  planDestino="Standard"
                  modulo="Tablero"
                  onClickUpgrade={() => setOpenUpgradeModal(true)}
                />
              )}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                  {/* <ResponsiveLineChart title="Valor Monetario" height={300}/> */}
                  <ResponsiveLineChart
                    title="Valor Monetario"
                    height={300}
                    isFullScreen={isLineChartFullScreen}
                    onToggleFullScreen={() => setIsLineChartFullScreen((prev) => !prev)}
                    containerRef={graficasContainerRef}
                    isFreemium={isFreemium}
                    onUpgradeClick={() => setOpenUpgradeModal(true)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2} sx={{ height: "100%" }}>
                    <Grid item xs={12} sm={6}>
                      <StatCard
                        value={`${analytics?.totaIngresos || "0"} Bs.`}
                        label="Total Ingresos"
                        isFreemium={isFreemium}
                        gradient="linear-gradient(to right, rgba(167,243,208,1), rgba(110,231,183,1))"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StatCard
                        value={`${analytics?.totalEgresos || "0"} Bs.`}
                        label="Total Egresos"
                        isFreemium={isFreemium}
                        gradient="linear-gradient(to right, rgb(253, 228, 127), rgb(252, 178, 51))"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StatCard
                        value={`${analytics?.totalServicios || "0"} Servicios`}
                        label="Realizados"
                        isFreemium={isFreemium}
                        gradient="linear-gradient(to right, rgba(199,210,254,1), rgba(165,180,252,1))"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StatCard
                        value={`${analytics?.totalGanancias || "0"} Bs.`}
                        label="Total Ganancias"
                        isFreemium={isFreemium}
                        gradient="linear-gradient(to right, rgba(191,219,254,1), rgba(147,197,253,1))"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {isFreemium && (
                  <UpgradeStickyBanner
                    planActual="Freemium"
                    planDestino="Standard"
                    modulo="Tablero"
                    onClickUpgrade={() => setOpenUpgradeModal(true)}
                  />
                )}
                <Grid item xs={12} md={4}>
                  <ResponsiveDonutCard
                    title="Servicios En Sucursal"
                    // data={generarDatosFakePieChart(15, "Sucursal")}
                    data={
                      isDonutSucursalFull
                        ? analytics?.pieCharts?.totalServicesInBranch || browserData
                        : analytics?.pieCharts?.topServicesInBranch || browserData
                    }
                    palette={paletteDevice}
                    radius={0}
                    isFullScreen={isDonutSucursalFull}
                    onToggleFullScreen={() => setIsDonutSucursalFull((prev) => !prev)}
                    containerRef={graficasContainerRef}
                    isFreemium={isFreemium}
                    onUpgradeClick={() => setOpenUpgradeModal(true)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ResponsiveDonutCard
                    title="Tasa de retención Global"
                    data={hasData ? pieChartData : [{ label: "Sin datos disponibles", value: 1, percentage: 100 }]}
                    palette={paletteDevice}
                    radius={0}
                    isFullScreen={isDonutRetentionFull}
                    onToggleFullScreen={() => setIsDonutRetentionFull((prev) => !prev)}
                    containerRef={graficasContainerRef}
                    isFreemium={isFreemium}
                    onUpgradeClick={() => setOpenUpgradeModal(true)}
                    infoService={false}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ResponsiveDonutCard
                    title="Servicios A Domicilio"
                    data={
                      isDonutDomicilioFull
                        ? analytics?.pieCharts?.totalServicesDomicilio || browserData
                        : analytics?.pieCharts?.topServicesDomicilio || browserData
                    }
                    palette={paletteBrowser}
                    radius={50}
                    isFullScreen={isDonutDomicilioFull}
                    onToggleFullScreen={() => setIsDonutDomicilioFull((prev) => !prev)}
                    containerRef={graficasContainerRef}
                    isFreemium={isFreemium}
                    onUpgradeClick={() => setOpenUpgradeModal(true)}
                  />
                </Grid>

                <Grid item xs={12} md={12}>
                  <ResponsiveBarChart
                    title="Información empleados"
                    isFullScreen={isBarChartFullScreen}
                    onToggleFullScreen={() => setIsBarChartFullScreen((prev) => !prev)}
                    containerRef={graficasContainerRef}
                    isFreemium={isFreemium}
                    onUpgradeClick={() => setOpenUpgradeModal(true)}
                  />
                </Grid>
              </Grid>
              {!(
                isLineChartFullScreen ||
                isDonutSucursalFull ||
                isDonutRetentionFull ||
                isDonutDomicilioFull ||
                isBarChartFullScreen
              ) && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {isFreemium && (
                    <UpgradeStickyBanner
                      planActual="Freemium"
                      planDestino="Standard"
                      modulo="Tablero"
                      onClickUpgrade={() => setOpenUpgradeModal(true)}
                    />
                  )}
                  <Grid item xs={12} md={6}>
                    <ResponsiveCustomBarChart
                      title="Top 6 clientes"
                      isFullScreen={isTopCustomersFullScreen}
                      onToggleFullScreen={() => setIsTopCustomersFullScreen((prev) => !prev)}
                      containerRef={graficasContainerRef}
                      isFreemium={isFreemium}
                      onUpgradeClick={() => setOpenUpgradeModal(true)}
                    />
                  </Grid>
                  {!isTopCustomersFullScreen && (
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 3,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)", // sombra más marcada pero elegante
                          border: "1px solid #e5e7eb", // borde gris claro (Tailwind gray-200)
                          overflow: "hidden", // para que todo quede limpio
                        }}
                      >
                        <ResourceContextProvider value="enterprise/customers">
                          <List
                            title="Tablero"
                            actions={false}
                            exporter={false}
                            pagination={false}
                            filter={computedProviderId ? { providerId: computedProviderId } : {}}
                          >
                            <Datagrid
                              // rowClick="show"
                              bulkActionButtons={false}
                              data={topCustomersDetail}
                              rowClick={(id, basePath, record) => {
                                // Retornamos la URL a la que queremos ir
                                return `/enterprise/customers/${record._id}`;
                              }}
                              sx={{
                                pointerEvents: isFreemium ? "none" : "auto",
                                opacity: isFreemium ? 0.5 : 1,
                                "& .RaDatagrid-cell": {
                                  textAlign: "center",
                                },
                                "& .RaDatagrid-headerCell": {
                                  padding: "15px 16px", // Ajusta según tu diseño
                                },
                              }}
                            >
                              <TextField source="fullName" label="Nombre Completo" />
                              <FunctionField label="Teléfono" render={(record) => <WhatsAppField record={record} />} />
                              {/* <TextField source="gender" label="Género" /> */}
                              <FunctionField
                                label="Calificación"
                                render={(record) =>
                                  record?.rating === undefined || record.rating === null || record.rating === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                      No tiene
                                    </Typography>
                                  ) : (
                                    <Rating
                                      name="customer-rating"
                                      value={record.rating}
                                      readOnly
                                      precision={0.5}
                                      sx={{ fontSize: "1.5rem" }}
                                    />
                                  )
                                }
                              />
                              <FunctionField
                                label="Historial"
                                render={(record) => <ServicesButton record={record} />}
                              />
                            </Datagrid>
                          </List>
                        </ResourceContextProvider>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}
            </div>
            <UpgradePlanModal
              open={openUpgradeModal}
              onClose={() => setOpenUpgradeModal(false)}
              planActual={user?.plan}
              planDestino="standard"
            />
          </Box>
        </ThemeProvider>
      </StyledEngineProvider>
    </Firebase>
  );
};

export default Dashboard;
